/**
*
* InputContainer
*
*/

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  INVALID_COMPONENT_CLASS,
  LABEL_SUFFIX,
} from 'utils/constants';
import { generateHTMLIdFromKeyPath } from 'utils/idUtils';

const InputContainer = (props) => {
  const { keyPath = [], fieldType, label } = props;
  const childId = generateHTMLIdFromKeyPath(keyPath, fieldType);
  const getErrorTextForUIState = ({ touched, focused, isValid, errorDisplay, hasLostFocusOnce }) => {
    let errorText = '';
    if (touched && focused) {
      if (hasLostFocusOnce && !isValid) {
        errorText = errorDisplay;
      }
    } else if (touched && !isValid) {
      errorText = errorDisplay;
    }
    return errorText;
  };

  const { child, errorText } = useMemo(() => {
    const { uiState, children, disabled, label: labelValue, fieldType: fieldTypeValue } = props;
    const uiStateValue = uiState ?? {};
    const labelValueFinal = labelValue ?? '';
    const fieldTypeValueFinal = fieldTypeValue ?? 'tx-attr';

    const errorTextValue = getErrorTextForUIState(uiStateValue);
    const childIsInvalid = errorTextValue != null && errorTextValue.length > 0;

    const clonedChild = React.cloneElement(React.Children.only(children), {
      disabled,
      id: childId,
      invalid: childIsInvalid,
      labelledBy: labelValueFinal.length ? childId.concat(LABEL_SUFFIX) : '',
      fieldType: fieldTypeValueFinal,
    });

    return { child: clonedChild, errorText: errorTextValue };
  }, [props, childId]);

  const labelBoldClass = (props.boldLabel ?? true) ? 'font-weight-bold' : '';
  const disabledClass = (props.disabled ?? false) ? 'text-muted' : '';
  const labelComponent = (
    <label
      className={`${labelBoldClass} ${disabledClass} ${props.className ?? ''}`}
      id={`${childId}-label`}
      htmlFor={childId}
    >
      {label}{(props.uiState ?? {}).required ? ' *' : null}
    </label>
  );

  return (
    <div className={errorText ? INVALID_COMPONENT_CLASS : ''}>
      {!!(label ?? '').length && labelComponent}
      {child}
      <div className="invalid-feedback mt-2">
        {errorText}
      </div>
    </div>
  );
};

InputContainer.propTypes = {
  boldLabel: PropTypes.bool,
  children: PropTypes.node.isRequired, // eslint-disable-line react/no-unused-prop-types
  disabled: PropTypes.bool,
  fieldType: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  keyPath: PropTypes.array,
  uiState: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
};

export default InputContainer;
